import * as priceFragments from '@/services/price/fragments';
import service, { IMaterial } from '@/services/material';
import { store as DirtyCheckComposer } from '@/composers/dirty-check';
import { diffIdsRemoved, promiseError } from '@/store/utils';
import { IStateMaterial } from './types';

const formatMaterial = (data: IMaterial): IMaterial => (
  JSON.parse(
    JSON.stringify({
      name: '',
      care: '',
      color: '',
      composition: '',
      notes: '',
      origin: '',
      shrinkage: '',
      thickness: '',
      weight: '',
      width: '',
      sym_bleach: '',
      sym_dry_clean_solvent: '',
      sym_drying: '',
      sym_ironing_temp: '',
      sym_tumble_dry: '',
      sym_wash: '',
      ...data,

      // provide defaults for database nullable values
      prices: data.prices || [],
      properties: data.properties || [],
      vendors: data.vendors || [],
    }),
  )
);

export default () => DirtyCheckComposer({
  namespaced: true,
  state: () => ({
    data: {},
    saved: {},
    vendors: [] as any,
  } as IStateMaterial),
  mutations: {
    CREATE() {
      //
    },
    LOAD(state: IStateMaterial, material: IMaterial) {
      state.data = formatMaterial(material);
      state.saved = formatMaterial(material);
    },
    REMOVE(state: IStateMaterial) {
      // reset all data (prevents isDirty from being true)
      state.data = formatMaterial({} as IMaterial);
      state.saved = formatMaterial({} as IMaterial);
    },
    SAVE(state: IStateMaterial) {
      state.saved = {
        ...JSON.parse(
          JSON.stringify(state.data),
        ),
      };
    },
  },
  actions: {
    create: ({ commit }: any) => (
      service.create({
        name: 'Material name',
      })
      .then((created: any) => {
        commit('CREATE', created);
        return created;
      })
      .catch(promiseError('MATERIAL_CREATE', commit))
    ),
    load: ({ commit }: any, id: string) => (
      service
        .get(id, `
          id
          name
          care
          color
          composition
          notes
          origin
          properties
          shrinkage
          thickness
          weight
          width
          sym_bleach
          sym_dry_clean_solvent
          sym_drying
          sym_ironing_temp
          sym_tumble_dry
          sym_wash
          assets (
            order_by: {
              index: asc_nulls_last
            }
          ) {
            id
            src
            index
          }
          vendors (
            order_by: {
              vendor: {
                name: asc
              }
            }
          ) {
            id
            properties
            vendor {
              id
              name
            }
          },
          ${priceFragments.query()}
        `)
        .then(result => {
          commit('LOAD', result);
          return result;
        })
        .catch(promiseError('MATERIAL_LOAD', commit))
    ),
    remove: ({ commit }: any, id: string) => (
      service
        .remove(id)
        .then(() => commit('REMOVE'))
        .catch(promiseError('MATERIAL_REMOVE', commit))
    ),
    save: ({ commit, state }: any) => {
      const { data, saved } = state;
      return service
        .update(data.id, {
          material: {
            ...data,

            // assets are a database view not a linked record
            // therefore remove asset data
            assets: undefined,
            prices: undefined,
          },
          deleteVendorIds: diffIdsRemoved(data.vendors, saved.vendors),

          // add pricing payload
          priceRemoveIds: diffIdsRemoved(data.prices, saved.prices),
          prices: data.prices.map((price: any) => ({
            ...price,
            type: 'material',
            type_id: data.id,
          })),
        })
        .then(() => commit('SAVE'))
        .catch(promiseError('MATERIAL_SAVE', commit));
    },
  },
});
